import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

// Renders children inside a real <iframe> so that CSS media queries and viewport
// units resolve against the iframe's own width (mobile), making the editor preview
// match exactly what guests see on a phone.
export const IframePreview = ({ width = 390, height = 832, scale = 1, children }) => {
  const iframeRef = useRef(null);
  const observerRef = useRef(null);
  const seenRef = useRef(new Set());
  const [mountNode, setMountNode] = useState(null);

  const syncStyles = useCallback((doc) => {
    const seen = seenRef.current;
    document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => {
      const key = node.tagName === "LINK" ? node.getAttribute("href") : node.textContent;
      if (!key || seen.has(key)) return;
      seen.add(key);
      doc.head.appendChild(node.cloneNode(true));
    });
  }, []);

  // onLoad fires reliably for srcDoc iframes (unlike srcless about:blank iframes).
  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe && iframe.contentDocument;
    if (!doc) return;
    seenRef.current = new Set();
    if (!doc.querySelector('meta[name="viewport"]')) {
      const meta = doc.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1";
      doc.head.appendChild(meta);
    }
    syncStyles(doc);
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new MutationObserver(() => syncStyles(doc));
    observerRef.current.observe(document.head, { childList: true, subtree: true });
    // Hide the scrollbar so content fills the full width exactly like a real phone
    // (mobile browsers use 0-width overlay scrollbars). It stays scrollable via wheel/touch.
    if (!doc.getElementById("fn-preview-reset")) {
      const reset = doc.createElement("style");
      reset.id = "fn-preview-reset";
      reset.textContent =
        "html,body{scrollbar-width:none;-ms-overflow-style:none;}" +
        "html::-webkit-scrollbar,body::-webkit-scrollbar{width:0;height:0;display:none;}";
      doc.head.appendChild(reset);
    }
    doc.documentElement.style.background = "transparent";
    doc.body.style.margin = "0";
    doc.body.style.background = "transparent";
    setMountNode(doc.body);
  }, [syncStyles]);

  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return (
    <iframe
      ref={iframeRef}
      onLoad={handleLoad}
      title="Vista previa de la invitación"
      srcDoc="<!doctype html><html><head></head><body></body></html>"
      style={{
        width,
        height,
        border: "0",
        display: "block",
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      }}
    >
      {mountNode && createPortal(children, mountNode)}
    </iframe>
  );
};
